// MemoryPage — User-RAG-Memory (eve_memories) View · Add · Delete.

import { useEffect, useState } from 'react';
import { Brain, Plus, Trash2 } from 'lucide-react';
import { useEveMemory } from '@/hooks/use-eve-memory';

export function MemoryPage() {
  const { memories, loadMemories, addMemory, deleteMemory, clearMemories } = useEveMemory();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadMemories(true);
  }, [loadMemories]);

  const filtered = memories.filter((m) =>
    query.trim().length === 0 ? true : m.fact.toLowerCase().includes(query.toLowerCase()),
  );

  async function handleAdd() {
    if (draft.trim().length === 0 || saving) return;
    setSaving(true);
    setError(null);
    try {
      await addMemory(draft.trim());
      setDraft('');
      setAdding(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string | number) {
    if (!window.confirm('Diese Memory wirklich löschen?')) return;
    try {
      await deleteMemory(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen');
    }
  }

  async function handleClearAll() {
    if (memories.length === 0) return;
    if (!window.confirm(`Wirklich ALLE ${memories.length} Memories löschen? Nicht rückgängig.`)) return;
    try {
      await clearMemories();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Clear fehlgeschlagen');
    }
  }

  return (
    <div className="h-full overflow-y-auto p-5">
      <div className="mx-auto max-w-3xl space-y-4">
        <header className="flex items-center justify-between border-b border-white/5 pb-4">
          <div className="flex items-center gap-3">
            <Brain className="size-5 text-amber-400" />
            <div>
              <h1 className="text-xl font-bold">EVE Memory</h1>
              <p className="mt-0.5 text-[12px] text-neutral-500">
                {memories.length.toLocaleString('en-US')} Fakten — EVE nutzt sie als RAG-Kontext.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setAdding((s) => !s)}
              className="inline-flex items-center gap-1 rounded-lg border border-amber-500/30 bg-amber-500/15 px-3 py-1.5 text-[12px] font-bold text-amber-300 hover:bg-amber-500/25"
            >
              <Plus className="size-3" />
              Neue Memory
            </button>
            {memories.length > 0 ? (
              <button
                type="button"
                onClick={() => void handleClearAll()}
                className="rounded-lg border border-rose-500/20 px-2.5 py-1.5 text-[11px] text-rose-400/80 hover:bg-rose-500/10"
              >
                Alle löschen
              </button>
            ) : null}
          </div>
        </header>

        {error ? (
          <div className="rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-300">
            {error}
          </div>
        ) : null}

        {adding ? (
          <div className="glass-strong rounded-2xl p-4">
            <textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              placeholder='z.B. „Doc bevorzugt simple Prompts ohne overengineering" …'
              className="w-full rounded-lg border border-white/10 bg-neutral-900/60 px-3 py-2 text-[13px] outline-none focus:border-amber-500/40"
            />
            <div className="mt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setAdding(false);
                  setDraft('');
                }}
                className="rounded-lg border border-white/10 px-3 py-1.5 text-[12px] text-neutral-300 hover:bg-white/5"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void handleAdd()}
                disabled={draft.trim().length === 0 || saving}
                className="rounded-lg bg-amber-500 px-3 py-1.5 text-[12px] font-bold text-neutral-950 hover:bg-amber-400 disabled:bg-neutral-700 disabled:text-neutral-500"
              >
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
            </div>
          </div>
        ) : null}

        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Memories filtern…"
          className="w-full rounded-lg border border-white/10 bg-neutral-900/60 px-3 py-2 text-[12px] outline-none focus:border-amber-500/40"
        />

        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 p-12 text-center text-[12px] text-neutral-500">
            {memories.length === 0
              ? 'Noch keine Memories gespeichert. EVE extrahiert automatisch wichtige Fakten aus euren Gesprächen.'
              : 'Keine Memories passend zum Filter.'}
          </div>
        ) : (
          <ul className="space-y-2">
            {filtered.map((m) => (
              <li
                key={m.id}
                className="glass flex items-start gap-3 rounded-xl border border-white/5 p-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] leading-relaxed text-neutral-200">{m.fact}</p>
                  <div className="mt-1 flex items-center gap-2 text-[10px] text-neutral-500">
                    {m.date ? <span>{m.date}</span> : null}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => void handleDelete(m.id)}
                  aria-label="Memory löschen"
                  className="shrink-0 text-neutral-500 hover:text-rose-400"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
